// The three moves in the whole game: give a clue, touch a card, stop.
// Everything cites docs/rules.pdf.

import { UNLIMITED, MAX_CLUE_NUMBER, extraGuess } from '../constants';

export const giveClueAction = (word, count) => ({ type: 'GIVE_CLUE', word, count });
export const guessAction = (index) => ({ type: 'GUESS', index });
export const endTurnAction = () => ({ type: 'END_TURN' });

const otherTeam = (team) => (team === 'red' ? 'blue' : 'red');

const teamWordsLeft = (state, team) => state.key
  .filter((identity, i) => identity === team && !state.revealed[i]).length;

/**
 * The one clue-legality rule the app enforces: "Your clue cannot be any of
 * the codenames visible on the table" (p4) - visible meaning unrevealed;
 * "a clue that is not legal now might be legal later" once the word is
 * covered. Everything else (compound words, rhymes, proper names, p6-p7)
 * is a table judgment call the rulebook itself leaves to the players.
 */
export const clueProblem = (state, word) => {
  const cleaned = String(word || '').trim();
  if (!cleaned) return 'Give a one-word clue.';
  if (/\s/.test(cleaned)) return 'One word only.';
  const upper = cleaned.toUpperCase();
  const visible = state.board.some((w, i) => !state.revealed[i] && w === upper);
  if (visible) return "Your clue can't be a codename still on the table.";
  return null;
};

const finish = (state, winner, endedBy, line) => ({
  ...state,
  turn: { ...state.turn, phase: 'clue', clue: null },
  log: [...state.log, line],
  meta: { ...state.meta, phase: 'finished', winner, endedBy },
});

function applyClue(state, action) {
  if (state.meta.phase !== 'playing' || state.turn.phase !== 'clue') return state;
  if (clueProblem(state, action.word)) return state;
  const count = action.count === UNLIMITED ? UNLIMITED
    : Math.max(0, Math.min(MAX_CLUE_NUMBER, Number(action.count) || 0));
  // 0 and unlimited both lift the cap; operatives still must guess at least
  // once and may keep going while they're right (p7, both expert clues).
  const guessesLeft = (count === UNLIMITED || count === 0) ? null : extraGuess(count);
  const word = String(action.word).trim().toUpperCase();
  return {
    ...state,
    turn: {
      ...state.turn,
      phase: 'guess',
      clue: { word, count },
      guessesLeft,
      guessedThisTurn: 0,
    },
    log: [...state.log, {
      kind: 'clue',
      team: state.turn.team,
      word,
      count,
    }],
    meta: { ...state.meta, seq: state.meta.seq + 1 },
  };
}

function applyGuess(state, action) {
  if (state.meta.phase !== 'playing' || state.turn.phase !== 'guess') return state;
  const index = Number(action.index);
  if (!(index >= 0 && index < state.board.length) || state.revealed[index]) return state;

  const team = state.turn.team;
  const identity = state.key[index];
  const revealed = state.revealed.slice();
  revealed[index] = true;
  const line = {
    kind: 'guess', team, word: state.board[index], identity,
  };
  let next = {
    ...state,
    revealed,
    log: [...state.log, line],
    meta: { ...state.meta, seq: state.meta.seq + 1 },
  };

  // "the team that contacted the assassin loses" (p4) - the game ends at
  // once, and the other team wins.
  if (identity === 'assassin') {
    return finish(next, otherTeam(team), 'assassin', { kind: 'end', winner: otherTeam(team), by: 'assassin' });
  }

  // "The game ends when one team has all their words covered. That team
  // wins. It is possible to win on the other team's turn" (p5) - so check
  // BOTH teams: revealing an opponent's last agent hands them the game.
  for (const t of ['red', 'blue']) {
    if (teamWordsLeft(next, t) === 0) {
      return finish(next, t, 'agents', { kind: 'end', winner: t, by: 'agents' });
    }
  }

  if (identity === team) {
    // "The operatives get another guess (but not another clue)" (p4).
    const guessesLeft = next.turn.guessesLeft === null ? null : next.turn.guessesLeft - 1;
    const exhausted = guessesLeft !== null && guessesLeft <= 0;
    return {
      ...next,
      turn: exhausted
        ? { team: otherTeam(team), phase: 'clue', clue: null, guessesLeft: 0, guessedThisTurn: 0 }
        : { ...next.turn, guessesLeft, guessedThisTurn: next.turn.guessedThisTurn + 1 },
    };
  }

  // Bystander or the other team's agent: "This ends the turn" (p4).
  return {
    ...next,
    turn: { team: otherTeam(team), phase: 'clue', clue: null, guessesLeft: 0, guessedThisTurn: 0 },
  };
}

function applyEndTurn(state) {
  if (state.meta.phase !== 'playing' || state.turn.phase !== 'guess') return state;
  // "The field operatives must always make at least one guess" (p4); "You
  // can stop guessing at any time" after that (p4).
  if (state.turn.guessedThisTurn < 1) return state;
  return {
    ...state,
    turn: {
      team: otherTeam(state.turn.team), phase: 'clue', clue: null, guessesLeft: 0, guessedThisTurn: 0,
    },
    log: [...state.log, { kind: 'pass', team: state.turn.team }],
    meta: { ...state.meta, seq: state.meta.seq + 1 },
  };
}

export function dispatch(state, action) {
  switch (action?.type) {
    case 'GIVE_CLUE': return applyClue(state, action);
    case 'GUESS': return applyGuess(state, action);
    case 'END_TURN': return applyEndTurn(state);
    default: return state;
  }
}

export { otherTeam, teamWordsLeft };
